import { useSetWatcher, useTicketWatchers } from '@servicetim/ui/api';
import { AsyncPicker, Button, describe, ProblemBanner, toast } from '@servicetim/ui/components';
import { Plus, X } from 'lucide-react';
import { useState } from 'react';
import { UserChip } from './badges';
import { RailCard } from './rail-card';
import { useTicketContext } from './ticket-context';

/** Watchers list; anyone may (un)watch for themselves, `watcher.manage` for others (S-05-10). */
export function WatchersCard() {
  const { ticket, meId, loadUsers } = useTicketContext();
  const watchers = useTicketWatchers(ticket.id);
  const setWatcher = useSetWatcher(ticket.id);
  const [adding, setAdding] = useState(false);

  const change = (userId: string, watching: boolean) =>
    setWatcher.mutate(
      { userId, watching },
      {
        onSuccess: () => {
          setAdding(false);
          toast.success(watching ? 'Watcher added' : 'Watcher removed');
        },
        onError: (e) =>
          toast.error(describe(e).title, { description: describe(e).detail ?? undefined }),
      },
    );

  const items = watchers.data ?? [];
  return (
    <RailCard
      title={`Watchers${watchers.data ? ` (${items.length})` : ''}`}
      actions={
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setAdding((v) => !v)}
          aria-expanded={adding}
        >
          <Plus />
          Add
        </Button>
      }
    >
      {watchers.error ? <ProblemBanner error={watchers.error} size="sm" /> : null}
      {adding ? (
        <AsyncPicker
          value={undefined}
          onChange={(id) => {
            if (id) change(id, true);
          }}
          load={loadUsers}
          placeholder="Add a watcher…"
          searchPlaceholder="Search people…"
          disabled={setWatcher.isPending}
        />
      ) : null}
      {watchers.isSuccess && items.length === 0 ? (
        <p className="text-fg-muted">Nobody is watching yet.</p>
      ) : null}
      <ul className="space-y-1.5">
        {items.map((w) => (
          <li key={w.userId} className="flex items-center justify-between gap-2">
            <UserChip name={w.displayName} />
            <Button
              size="icon"
              variant="ghost"
              className="size-7"
              aria-label={`Remove ${w.displayName}${w.userId === meId ? ' (you)' : ''}`}
              onClick={() => change(w.userId, false)}
              disabled={setWatcher.isPending}
            >
              <X />
            </Button>
          </li>
        ))}
      </ul>
    </RailCard>
  );
}
